import Button from 'tio-reimagined/components/button';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import Popover from 'tio-reimagined/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack, Section } from 'tio-reimagined/components/layout';
import Component from '@glimmer/component';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import StatusText from 'tio-employee/components/slr/status-text';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import Tile from '../tile';
import TioLoadingSpinner from 'tio-common/components/tio/loading-spinner';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import urlFor from 'ember-router-helpers/helpers/url-for';
import type { IntlService } from 'ember-intl';
import type EmployeeService from 'tio-employee/services/employee';
import type SessionContextService from 'tio-employee/services/session-context';
import { PhWarning, PhCalendarDots } from 'ember-phosphor-icons';

export default class DashboardWidgetsStudentLoanRepaymentComponent extends Component {
  @service declare employee: EmployeeService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;

  get lastPaymentAmount() {
    return (this.employee.contributionPayments[0]?.amount || 0) / 100;
  }

  get lastPaymentDate() {
    return (
      this.employee.contributionPayments[0]?.transaction_date ||
      this.employee.contributionPayments[0]?.line_item_period ||
      ''
    );
  }

  get lastPaymentStatus() {
    if (this.employee.contributionPayments[0]?.transaction_date) {
      return this.intl.t('slr.payment_sent');
    } else if (this.employee.contributionPayments[0]?.line_item_date) {
      return this.intl.t('slr.in_progress');
    } else return this.intl.t('slr.pending');
  }

  get isLoading() {
    return (
      this.employee.contributionsState.isLoading ||
      this.employee.futurePayments.isLoading ||
      this.employee.documentsData.isLoading
    );
  }

  get nextContributionPayment() {
    return this.employee.futureContributionPayments[0];
  }

  get nextPaymentAmount() {
    return (this.nextContributionPayment?.amount || 0) / 100;
  }

  get nextPaymentDate() {
    return this.nextContributionPayment?.date || '';
  }

  get nextPaymentStatus() {
    const status = this.nextContributionPayment?.state || '';
    if (status === 'PENDING_APPROVAL') {
      return this.intl.t('slr.pending');
    }
    return status.replace(/_/g, ' ').toLowerCase();
  }

  get totalPayments() {
    return this.employee.contributionSummary?.total / 100 || 0;
  }

  get hasAccounts() {
    return !!this.sessionContext.user?.person?.accounts?.length;
  }

  get needsToLinkLoans() {
    return !this.hasAccounts && !this.isLoading && !this.employee.isPartiallyApplied;
  }

  get eligibleStartDate() {
    return this.employee.current.eligibilityStartDate || '';
  }

  get route() {
    return this.needsToLinkLoans
      ? 'authenticated.observability.upload'
      : 'authenticated.slr.dashboard';
  }

  get query() {
    return this.needsToLinkLoans ? { source: 'slr', providers: 'statement,nslds' } : {};
  }

  get hasError() {
    return !!this.employee.accountAlerts.length || !!this.employee.isPartiallyApplied;
  }

  get isMonthlyPlan() {
    return this.employee.current?.plan?.cadence === 'MONTHLY';
  }

  get showNextPayment() {
    return (
      this.isMonthlyPlan &&
      this.nextPaymentDate &&
      this.nextPaymentAmount &&
      !this.nextContributionPayment?.validation_errors?.length
    );
  }

  <template>
    <LinkTo @route={{this.route}} @query={{this.query}}>
      <Tile @headerText={{t "dashboard_tiles.student_loan_repayment"}}>
        <:body>
          {{#if this.isLoading}}
            <div class="my-auto"><TioLoadingSpinner /></div>
          {{else if this.needsToLinkLoans}}
            <VStack class="tio-stack-center min-h-0 text-center px-0">
              <h3 class="text-lg tio-fg-muted font-semibold">
                {{t "dashboard_tiles.no_loan_accounts"}}
              </h3>
              {{svgJar
                "coin-folder"
                width="40%"
                height="40%"
                role="img"
                desc=(t "svg.coin_folder")
              }}
              <p>
                {{t "dashboard_tiles.slr_link_loans"}}
                {{#if this.eligibleStartDate}}
                  <Popover @triggerMode="hover">
                    <:trigger as |pop|>
                      <span class="underline" {{pop.register}} {{pop.hoverHandlers}}>{{t
                          "dashboard_tiles.eligible_start_date"
                        }}</span>
                    </:trigger>
                    <:body>
                      <div class="p-2">
                        <h3 class="font-semibold">
                          {{t "dashboard_tiles.slr_start_date_tip_header"}}
                        </h3>
                        <p>
                          {{t "dashboard_tiles.slr_start_date_tip_body"}}
                          {{safeFormatDate this.eligibleStartDate month="short" year="numeric"}}
                        </p>
                      </div>
                    </:body>
                  </Popover>
                {{else}}
                  {{t "dashboard_tiles.eligible_start_date"}}.
                {{/if}}
              </p>
              <Button
                @intent="primary"
                @appearance="outlined"
                {{on
                  "click"
                  (transitionTo
                    (concat
                      (urlFor "authenticated.observability.upload")
                      "?source=slr&providers=statement,nslds"
                    )
                  )
                }}
              >
                {{t "dashboard_tiles.slr_link_loans_btn_text"}}
              </Button>
            </VStack>
          {{else if this.hasError}}
            <VStack class="tio-stack-center gap-y-2 min-h-0 text-center px-0">
              <PhWarning @size="32" class="tio-fg-warning" />
              <h3 class="text-xl">{{t "dashboard_tiles.loan_account_issue"}}</h3>
              <p>{{t "dashboard_tiles.loan_account_issue_body"}}</p>
              <Button
                @intent="primary"
                @appearance="outlined"
                {{on "click" (transitionTo "authenticated.slr.dashboard")}}
              >
                {{t "common.learn_more"}}
              </Button>
            </VStack>
          {{else}}
            <VStack class="p-0!" @collapsed={{true}}>
              <Section>
                <h3 class="text-lg">
                  {{t "dashboard_tiles.slr_employer_contributions"}}
                </h3>
                <span class="tio-fg-brand font-semibold">
                  {{t "slr.total"}}:
                  {{safeFormatNumber this.totalPayments style="currency" currency="USD"}}
                </span>
              </Section>
              {{#if this.lastPaymentAmount}}
                <Section class="flex flex-col gap-y-1">
                  <h3 class="text-lg">
                    {{t "common.last"}}:
                    <span class="font-semibold">{{safeFormatNumber
                        this.lastPaymentAmount
                        style="currency"
                        currency="USD"
                      }}</span>
                  </h3>
                  <StatusText
                    @status={{this.lastPaymentStatus}}
                    @lastPaymentDate={{this.lastPaymentDate}}
                  />
                  <div class="flex tio-fg-muted">
                    <PhCalendarDots @size="24" />
                    {{safeFormatDate this.lastPaymentDate month="short" year="numeric"}}
                  </div>
                </Section>
              {{/if}}
              {{#if this.showNextPayment}}
                <Section class="flex flex-col gap-y-1">
                  <h3 class="text-lg">
                    {{t "common.next"}}:
                    <span class="font-semibold">{{safeFormatNumber
                        this.nextPaymentAmount
                        style="currency"
                        currency="USD"
                      }}</span>
                  </h3>
                  <StatusText
                    @status={{this.nextPaymentStatus}}
                    @lastPaymentDate={{this.nextPaymentDate}}
                  />
                  <div class="flex tio-fg-muted">
                    <PhCalendarDots @size="24" />
                    {{safeFormatDate this.nextPaymentDate month="short" year="numeric"}}
                  </div>
                </Section>
              {{/if}}
            </VStack>
          {{/if}}
        </:body>
      </Tile>
    </LinkTo>
  </template>
}
